/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/servicesDetailsDialogStyle';
import CustomTable from 'components/CustomTable/CustomTable';
import { PhoneNumberCell, TextCell } from 'components/Cells/Cells';
import { BodyS, BodySBold } from 'components/Typography/Typography';
import SetOfNumbersDialog from './SetOfNumbersDialog';
import Button from 'components/CustomButtons/Button';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import CustomPopUpWithLoader from 'components/CustomPopUpWithLoader/CustomPopUpWithLoader';
import { LoyaltyGroup } from 'utils/api/models/prepaidLoyalty/loyaltyGroup';
import { LoyaltyGroupService } from 'services/loyaltyGroups/loyaltyGroups';
import { finalize } from 'rxjs/internal/operators';
import { GroupSubscriptions } from 'utils/api/models/prepaidLoyalty/groupSubscriptions';
import moment from 'moment';
import { getRecurrence, getServiceType } from 'views/TopUpAddOns/helpers/helperFunctions';
import { SubscriptionDetails } from 'utils/api/models/prepaidLoyalty/subscriptionDetails';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@material-ui/icons/KeyboardArrowUpRounded';
import { serviceType, SingleSubscription, subscriptionStatus } from 'utils/api/models/topUpAddons/singleSubscription';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ServiceDetailsTable from './ServiceDetailsTable';

const CREDIT_CARD = 'CREDIT_CARD';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IGroupDetailsDialog {
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	refreshGroups: () => void;
	group?: LoyaltyGroup;
}

const ServicesDetailsDialog = ({ isOpen, setOpen, group, refreshGroups }: IGroupDetailsDialog) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [numbersDialog, setNumbersDialog] = useState<boolean>(false);
	const [currentGroup, setCurrentGroup] = useState<LoyaltyGroup | undefined>();
	const [currentGroupSubscriptions, setCurrentGroupSubscriptions] = useState<GroupSubscriptions | undefined>();
	const [currentSubscription, setCurrentSubscription] = useState<SingleSubscription | undefined>();
	const [unsubscribeDialog, setUnsubscribeDialog] = useState<boolean>(false);
	const [popUpUnsubscriptionOpen, setPopUpUnsubscriptionOpen] = useState<boolean>(false);
	const [popUpUnsubscriptionState, setPopUpUnsubscriptionState] = useState<boolean>(false);
	const [expanded, setExpanded] = useState<any>({});
	const classes = useStyles();

	useEffect(() => {
		if (group) {
			setCurrentGroup(group);
		}
	}, [group]);

	const groupSubscriptions = () => {
		if (currentGroup) {
			setLoading(true);
			LoyaltyGroupService.getGroupSubscriptions(currentGroup.id)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe((subscriptions) => {
					setCurrentGroupSubscriptions(subscriptions);
				});
		}
	};

	useEffect(() => {
		groupSubscriptions();
	}, [currentGroup]);

	const startUnsubscribe = (subscription: SingleSubscription) => {
		setOpen(false);
		setCurrentSubscription(subscription);
		setUnsubscribeDialog(true);
	};

	const hideDialogNumbers = () => {
		setNumbersDialog(false);
		setOpen(true);
	};

	const hideUnsubscribeDialog = () => {
		setUnsubscribeDialog(false);
	};

	const hideUnsubscriptionPopUp = () => {
		refreshGroups();
		setPopUpUnsubscriptionOpen(false);
	};

	const confirmUnsubscribe = () => {
		if (currentSubscription?.id) {
			setLoading(true);
			LoyaltyGroupService.unsubscribeSubscription(currentSubscription.id)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(() => {
					setUnsubscribeDialog(false);
					setPopUpUnsubscriptionState(true);
					setPopUpUnsubscriptionOpen(true);
				});
		}
	};
	return (
		<>
			<CustomDialog
				header={translate('prepaid.loyalty.manage.service.details')}
				description={<BodyS color="secondary-light">{currentGroup?.name || ''}</BodyS>}
				isOpen={isOpen}
				hideDialog={() => setOpen(false)}
				loading={loading}
				width={1000}
				dialogBody={
					<>
						<div className={classes.tableWrapper}>
							<CustomTable
								onPaggingChange={() => {
									setExpanded({});
								}}
								showPaginationTop={true}
								total={currentGroupSubscriptions?.subscriptions?.length || 0}
								tableProps={{
									data: currentGroupSubscriptions?.subscriptions || [],
									columns: [
										{
											Header: translate(
												'prepaid.loyalty.manage.service.details.table.subscriptions.name'
											),
											Cell: ({ original }: { original: SingleSubscription }) => (
												<TextCell
													color="primary-light"
													text={getServiceType(original.type) || ''}
												/>
											),
											accessor: 'type',
										},
										{
											Header: translate(
												'prepaid.loyalty.manage.service.details.table.subscriptions.amount'
											),
											Cell: ({ original }: { original: SingleSubscription }) => (
												<TextCell
													text={
														`${original.totalAmount?.toString()}${translate('OMR')}` || ''
													}
												/>
											),
											accessor: 'totalAmount',
										},
										{
											Header: translate(
												'prepaid.loyalty.manage.service.details.table.subscriptions.numbers'
											),
											Cell: ({ original }: { original: SingleSubscription }) => (
												<PhoneNumberCell
													color="brand"
													bold
													text={String(original?.numbersCount) || ''}
													onClick={() => {
														setCurrentSubscription(original);
														setOpen(false);
														setNumbersDialog(true);
													}}
												/>
											),
											accessor: 'numbersCount',
										},
										{
											Header: translate(
												'prepaid.loyalty.manage.service.details.table.subscriptions.recurrence'
											),
											Cell: ({ original }: { original: SingleSubscription }) => (
												<TextCell text={getRecurrence(original.recurrence) || ''} />
											),
											accessor: 'recurrence',
										},
										{
											Header: translate(
												'prepaid.loyalty.manage.service.details.table.subscriptions.end.date'
											),
											Cell: ({ original }: { original: SingleSubscription }) => (
												<TextCell text={moment(original.endDate).format('DD.MM.YYYY')} />
											),
											width: 100,
											accessor: 'endDate',
										},
										{
											Header: translate(
												'prepaid.loyalty.manage.service.details.table.subscriptions.payment'
											),
											Cell: ({ original }: { original: SingleSubscription }) => (
												<TextCell
													text={
														original.paymentType.toString() === CREDIT_CARD
															? translate('prepaid.loyalty.add.new.services.credit.card')
															: translate(
																	'prepaid.loyalty.add.new.services.payment.method.bill'
															  )
													}
												/>
											),
											width: 100,
											accessor: 'paymentType',
										},
										{
											Header: '',
											Cell: ({ original }: { original: SingleSubscription }) => (
												<span className={classes.unsubscribe}>
													<ConditionalRender
														show={original?.status === subscriptionStatus.ACTIVE}
														onTrue={() => (
															<Button
																color="tertiary-danger"
																onClick={() => startUnsubscribe(original)}
															>
																<>
																	<span>&#x2716;</span>
																	{translate('unsubscribe')}
																</>
															</Button>
														)}
														onFalse={() => (
															<Button color="tertiary-gray" disabled>
																{translate('finished')}
															</Button>
														)}
													/>
												</span>
											),
											accessor: 'status',
											width: 120,
										},
										{
											expander: true,
											width: 40,
											Expander: (expander: {
												original: types.prepaidLoyalty.ISubscription;
												isExpanded: boolean;
											}) => {
												return (
													<div className={classes.arrowWrapper}>
														<ConditionalRender
															show={
																serviceType[expander?.original?.type || ''] !==
																serviceType.TOP_UP
															}
															onTrue={() => (
																<>
																	{expander?.isExpanded ? (
																		<KeyboardArrowUpRoundedIcon />
																	) : (
																		<KeyboardArrowDownRoundedIcon />
																	)}
																</>
															)}
														/>
													</div>
												);
											},
										},
									],
									defaultPageSize: 5,
									SubComponent: ({ original }: { original: SubscriptionDetails }) => {
										return (
											<ConditionalRender
												show={original?.type !== serviceType.TOP_UP}
												onTrue={() => (
													<ServiceDetailsTable
														key={original.id}
														setLoading={setLoading}
														target={original?.target || ''}
													/>
												)}
											/>
										);
									},
									expanded: expanded,
									onExpandedChange: (exp: any) => {
										setExpanded(exp);
									},
									stroke: true,
								}}
							/>
						</div>
					</>
				}
			/>
			<SetOfNumbersDialog
				subscription={currentSubscription}
				hideDialog={hideDialogNumbers}
				isOpen={numbersDialog}
				groupSubscriptions={groupSubscriptions}
			/>

			<ConfirmationDialog
				isOpen={unsubscribeDialog}
				hideDialog={hideUnsubscribeDialog}
				header={''}
				loading={loading}
				dialogBody={
					<>
						<ConditionalRender
							show={currentSubscription?.paymentType.toString() === CREDIT_CARD}
							onTrue={() => (
								<BodySBold color="brand-secondary">
									{translate(
										'prepaid.loyalty.payment.type.card.upfront',
										moment(currentSubscription?.endDate).format('DD/MM/YYYY')
									)}
								</BodySBold>
							)}
						/>
						<BodyS>
							{translate(
								'prepaid.loyalty.manage.service.unsubscribe.dialog.description.topup',
								'',
								currentSubscription?.totalAmount?.toString() || ''
							)}
						</BodyS>
					</>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: hideUnsubscribeDialog,
						color: 'tertiary-light',
					},
					{
						text: translate('unsubscribe'),
						onClick: confirmUnsubscribe,
						color: 'primary-light',
					},
				]}
			/>

			<CustomPopUpWithLoader
				isOpen={popUpUnsubscriptionOpen}
				hideDialog={hideUnsubscriptionPopUp}
				loading={loading}
				isError={!popUpUnsubscriptionState}
				header={
					!popUpUnsubscriptionState
						? [translate('prepaid.loyalty.manage.service.unsubscribe.dialog.error.description')]
						: []
				}
				body={
					!popUpUnsubscriptionState ? (
						<>
							{translate(`prepaid.loyalty.manage.service.unsubscribe.dialog.error`)}
							<br />
							{translate(`prepaid.loyalty.manage.service.unsubscribe.dialog.error.call`)}
						</>
					) : (
						translate(`prepaid.loyalty.manage.service.unsubscribe.dialog.success`)
					)
				}
			/>
		</>
	);
};

export default ServicesDetailsDialog;
